/**
 * BookLanguageRowPicker — 选一条 **per-locale novel 语言行**（不是书组）的通用选择器。
 *
 * books 是 per-locale 建模（ADR-005）：`novels` 按语言分行，列表返回书组、每组带 `languages[]`。
 * 消费方要的是其中**某一行的 novel_id**，所以本组件把书组摊平成语言行候选。
 *
 * 语言范围由 `lockedLocale` 表达，三种形态（语义不同，不能互相替代）：
 *
 * - **不传**（落地页「投放书籍」）：摊平全部语言行，由人自己挑；页面语言随后**跟随所选行**。
 * - **单个 locale**（banner slide）：语言已由 slide 所在位决定，候选**被它反向约束**。
 * - **locale 数组**（推送任务）：目标 App 的 `allowed_languages` —— payload 是一个固定 `book_id`、
 *   没有 per-user 重映射，选到该 App 不支持的语言行就会推出一条用户打不开的深链。
 *
 * 上架状态过滤是**另一个维度**，由 `listedOnly` 单独控制（依据是写侧校不校 status，与语言无关）。
 *
 * 语言收窄两层缺一不可：
 *   ① 查询带 `lang` —— 只捞「有这些语言行」的书组，否则分页下会漏掉同语言但首页没出现的书；
 *   ② 摊平后再按 locale 过滤 —— `lang` 筛的是**书组**，返回的 `languages[]` 仍是全部语言行，
 *      不过滤则候选里混着范围外的语言行，人照样选得到错的那条。
 *
 * 为什么 banner 侧必须锁：C 端 serving 判据是
 * `deleted_at IS NULL ∧ novels.locale = :locale ∧ (override.status ?? status) = 'LISTED'`
 * （`novel-app-server` `services/banners-service.ts` 的 `isBookServable`）——
 * 语言行对不上就**静默过滤掉该 slide、不报错不下发**，手输 UUID 时运营完全看不出选错了。
 */

import type { NovelResponse } from '@novel-admin/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { booksApi } from '~/hooks/use-books';
import { BOOK_SEARCH_PLACEHOLDER, bookCodeDisplay } from '~/lib/book-identity';
import { BookIdentityCard } from '~/ui/BookIdentityCard';
import { LangChip } from '~/ui/LangChip';
import { SearchInput } from '~/ui/SearchInput';

export function BookLanguageRowPicker({
  value,
  book,
  onChange,
  lockedLocale,
  listedOnly,
  requireCover,
  bookLookupFailed,
  landingProductId,
  disabled = false,
}: {
  /** 已选中的 novel 行 id（per-locale 行，不是书组 id）*/
  value: string;
  /** 已选行的详情，由调用方查（落地页外部还要用它的 locale，故不吞进本组件）*/
  book: NovelResponse | undefined;
  onChange: (novelId: string) => void;
  /** 候选的语言范围：单个 locale（banner，锁定）/ 数组 / 不传（落地页、推送）。见文件头 */
  lockedLocale?: string | string[];
  /**
   * 只列 `status=LISTED` 的书行。**与语言范围解耦** —— 两者的依据完全不同：
   *
   * - banner slide 传 `true`：C 端 `isBookServable` 要求 effective status 为 LISTED，而写侧
   *   `assertBookLocaleForSlide` 只校验存在性与 locale，HIDDEN/DELISTED 存得进去却静默不下发。
   * - 书架推荐**不传**：写侧 `shelf-recommendations-admin-service` 全文不含 status 校验，
   *   只校验「存在且未软删」—— 即**书未上架也能先配草稿推荐**，
   *   加这层会砍掉那条合法路径。
   */
  listedOnly?: boolean;
  /**
   * 只列有封面的书行（banner slide 书籍目标，A2 §4.2）：写侧按 `cover_url` 去首尾空白后非空复校，
   * 不滤的话运营选得到、保存才被拒。按摊平后的语言行过滤（列表没有封面查询参数），
   * 整页都是无封面行时靠搜索定位。不影响已选值回显：既有 slide 可保留无封面目标。
   */
  requireCover?: boolean;
  /** 投放专用资格过滤：服务端按产品所有 App 的有效可见性排除下架语言行。 */
  landingProductId?: string;
  disabled?: boolean;
  /**
   * 已选行详情**确实查失败了**（不是还在查）。缺它就分不清两者 —— 冷缓存下 `book` 一开始恒为
   * undefined，不区分的话每次打开都会闪一下「可能已删除」的警告，属误报。
   */
  bookLookupFailed?: boolean;
}) {
  // 归一成数组：下面两层收窄与文案都按它走。
  // **按 scopeKey memo 化**：调用方常传字面量数组（每次渲染新引用），不 memo 会让下游 useMemo
  // 每帧重算；同时让依赖数组直接写 localeScope、与闭包里实际读的东西一致（lint 要求）。
  const scopeKey =
    lockedLocale === undefined
      ? null
      : Array.isArray(lockedLocale)
        ? lockedLocale.join(',')
        : lockedLocale;
  const localeScope = useMemo(
    () => (scopeKey === null ? undefined : scopeKey.split(',')),
    [scopeKey],
  );
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(!value);
  const booksQ = useQuery({
    // 语言范围进 key：同一搜索词在不同范围下候选集不同，不能共用缓存
    queryKey: ['books', 'language-row-picker', q, scopeKey, listedOnly ?? false, landingProductId],
    queryFn: () =>
      booksApi.listOffsetPaged(1, 40, {
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(landingProductId ? { landing_product_id: landingProductId } : {}),
        // 第一层收窄：只捞有该语言行的书组（分页下不漏，见文件头）
        ...(localeScope ? { lang: localeScope } : {}),
        // 第三层：只列 LISTED。C 端 `isBookServable` 要求 effective status 为 LISTED，而写侧
        // `assertBookLocaleForSlide` **只校验存在性与 locale** —— HIDDEN/DELISTED 的行保存得进去，
        // slide 却静默不下发，与选错语言行同一失败形态。
        // ⚠️ 这一层挡的是**全局** status；per-App override（`novel_app_overrides`）挡不住 ——
        // admin 的 books 列表既不暴露 per-app 状态也不支持按 App 过滤，见 PR body 的 Follow-up。
        ...(listedOnly ? { status: 'LISTED' as const } : {}),
      }),
    enabled: open && !disabled,
  });
  const list = useMemo(() => {
    const rows: {
      id: string;
      groupLabel: string;
      locale: string;
      /** 书组中文识别名（`cn_title`），候选的主名 */
      cnTitle: string;
      /** 该语言行的原文书名，与 cnTitle 不同时作次要行 */
      localeTitle: string;
    }[] = [];
    for (const g of booksQ.data?.data ?? []) {
      for (const lang of g.languages) {
        // 第二层收窄：lang 查询筛的是书组，languages[] 仍含全部语言行
        if (localeScope && !localeScope.includes(lang.locale)) continue;
        if (requireCover && lang.cover_url.trim() === '') continue;
        rows.push({
          id: lang.novel_id,
          groupLabel: bookCodeDisplay({
            bookCode: lang.book_code,
            bookGroupCode: g.book_group_code,
            locale: lang.locale,
          }),
          locale: lang.locale,
          // 主名用书组的 `cn_title`（中文运营识别名、跨 locale 稳定，dd#970）：候选里混着各语言
          // 译名时，光靠译名分不清同组的不同语言行，也分不清不同书组恰好撞名的译名。
          // 这一列是唯一跨 locale 稳定的识别锚。
          cnTitle: g.cn_title,
          localeTitle: lang.title,
        });
      }
    }
    return rows;
  }, [booksQ.data, localeScope, requireCover]);

  return (
    <div>
      <div className="hstack" style={{ gap: 8 }}>
        <BookIdentityCard
          book={book}
          badge={
            /* 既有 slide 可能指向 locale 不匹配的历史死链 —— 服务端刻意允许它存在
               （未上架时跳过 locale 校验，「否则无法下架 locale 错的死链」）。
               顶部回显不经候选过滤，故这里必须标出来，否则运营看不出现值就是错的那条。 */
            book && localeScope && !localeScope.includes(book.locale) ? (
              <span className="chip chip-warn" style={{ fontSize: 9 }}>
                语言不匹配 · 仅限 {localeScope.join(' / ')}
              </span>
            ) : book && requireCover && book.cover_url.trim() === '' ? (
              <span className="chip chip-warn" style={{ fontSize: 9 }}>
                无封面 · 不能改设为此书
              </span>
            ) : undefined
          }
          fallback={
            <span
              style={{
                fontSize: 12,
                color: bookLookupFailed ? 'var(--fg-warn)' : 'var(--fg-faint)',
              }}
            >
              {/* 确实查不到时**必须回显存着的 id**：软删 / 查询失败都会走到这里，而手输框时代
                  那个 UUID 一直摆在框里看得见，一直显示「加载书行…」等于把现值藏了。
                  但**只在真失败时才这么说** —— 还在查的时候报「可能已删除」是误报。 */}
              {!value ? (
                '尚未选择书籍语言行'
              ) : bookLookupFailed ? (
                <>
                  当前书行 <span className="mono">{value.slice(0, 8)}</span> 未能载入 ——
                  可能已删除或查询失败；重选会替换掉它
                </>
              ) : (
                '加载书行…'
              )}
            </span>
          }
        />
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? '收起' : '更换书籍'}
        </button>
      </div>
      {disabled && <p className="muted">请先选择投放产品，再选择该产品可用的书籍</p>}
      {open && !disabled && (
        <div className="card-flat" style={{ marginTop: 8, padding: 8 }}>
          <SearchInput
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={BOOK_SEARCH_PLACEHOLDER}
            size="sm"
          />
          <div style={{ maxHeight: 250, overflow: 'auto', marginTop: 6 }}>
            {list.map((r) => (
              <div
                key={r.id}
                onClick={() => {
                  onChange(r.id);
                  setOpen(false);
                }}
                className="hstack"
                style={{
                  gap: 8,
                  padding: '6px 8px',
                  borderRadius: 5,
                  cursor: 'pointer',
                  background: r.id === value ? 'var(--bg-subtle)' : 'transparent',
                }}
              >
                <span
                  className="mono"
                  style={{
                    fontSize: 11.5,
                    width: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.groupLabel}
                </span>
                <LangChip code={r.locale} style={{ fontSize: 9.5 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 11.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.cnTitle}
                  </div>
                  {/* 原文书名只在与中文识别名**不同**时才出一行：相同时（中文原创书）再画一遍
                      纯属噪音，还会把可见候选数腰斩。 */}
                  {r.localeTitle && r.localeTitle !== r.cnTitle && (
                    <div
                      style={{
                        fontSize: 10.5,
                        color: 'var(--fg-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.localeTitle}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {booksQ.isError && <p role="alert">书籍加载失败：{booksQ.error.message}</p>}
            {booksQ.isLoading && <p className="muted">加载可用书籍…</p>}
            {open && !booksQ.isError && !booksQ.isLoading && list.length === 0 && (
              // 空态写清收窄条件：锁定语言时「没有匹配的书行」极易被读成「这本书不存在」，
              // 实际成因通常是该书没有这个语言的行 —— 不说明的话运营无从诊断
              <div style={{ padding: 12, fontSize: 12, color: 'var(--fg-muted)' }}>
                {localeScope
                  ? `没有匹配的 ${localeScope.join(' / ')} 语言行 · 只能投放这些语言${requireCover ? '且有封面' : ''}的书行`
                  : landingProductId
                    ? '没有匹配的可投放书行 · 已排除该产品下架的书籍'
                    : '没有匹配的书行'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
